import { useFirestore } from "@/hooks/firestore/v2/useFirestore";
import {
  racingEvent,
  type RacingEvent,
} from "@/hooks/firestore/v2/models/racingEvent";
import { useMemo } from "react";
import { useCampaigns } from "@/hooks/firestore/v2/user/useCampaigns";
import {
  processRacingEvent,
  regionMap,
} from "@/hooks/firestore/v2/betting/shared";
import { useTokens } from "@/hooks/firestore/v2/user/useTokens";
import { usePermissions } from "@/hooks/useHasPermission";
import {
  limit,
  orderBy,
  type QueryConstraint,
  where,
} from "firebase/firestore";
import { useUserEventFilters } from "@/hooks/firestore";
import type { Always } from "@/types/utilities";

type RacingEventOptions = {
  titles?: string[];
  page?: number;
  regions?: ("au" | "world")[];
  meetingId?: string;
  status?: RacingEvent["status"];
};

const getRacingEventsQuery = ({
  titles,
  page,
  regions,
  eventFilters,
  meetingId,
  status,
}: RacingEventOptions & {
  eventFilters: string[];
}) => {
  const queries: QueryConstraint[] = [where("visible", "==", true)];
  const safeRegions = (regions || []).map((region) => regionMap[region]);

  if (status) {
    queries.push(where("status", "==", status));
  }

  if (safeRegions.length > 0) {
    queries.push(where("region", "in", safeRegions));
  }

  if (titles && titles.length > 0) {
    queries.push(
      where(
        "sport",
        "in",
        titles.map((title) => title.toUpperCase()),
      ),
    );
  }

  if (eventFilters?.length > 0) {
    queries.push(where("filters", "array-contains-any", eventFilters));
  }

  queries.push(orderBy("scheduledStartTimeTs", "asc"));

  if (page) {
    queries.push(limit(50 * page));
  }

  if (meetingId) {
    queries.push(where("meetingId", "==", meetingId));
  }

  return queries;
};

const useRacingEvents = ({
  regions,
  titles,
  page,
  status,
  meetingId,
}: RacingEventOptions) => {
  const eventFilters = useUserEventFilters();

  const {
    data: events,
    error,
    isLoading: eventsLoading,
  } = useFirestore(`/racingEvents`, racingEvent.array(), {
    query: getRacingEventsQuery({
      titles,
      regions,
      page,
      eventFilters,
      status,
      meetingId,
    }),
    keyPath: "id",
  });

  const { tokens } = useTokens();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const processedEvents = useMemo(() => {
    if (!events || !codes) return;

    return events.map((event) =>
      processRacingEvent(event, codes, tokens, permissions),
    );
  }, [events, codes]);

  return {
    events: processedEvents,
    error,
    isLoading: eventsLoading || codesLoading,
  };
};

type ProcessedRacingEvent = Always<
  ReturnType<typeof useRacingEvents>["events"]
>[0];

export type { ProcessedRacingEvent };
export { useRacingEvents };
